import cc from "classcat"
import numbro from "numbro"
import React, { useEffect, useState } from "react"
import { useFormContext } from "react-hook-form"

import styles from "../ProjectBudgetForm.module.css"

import currency, { formatCurrency } from "~/helpers/CurrencyHelper"
import { dashify } from "~/helpers/general-helpers"

import CurrencyInput from "~/common/CurrencyInput"
import Duration from "~/common/Duration"
import NumericInput from "~/common/NumericInput"
import { Delete } from "~/common/react-icons"

import { BudgetedRole, ProjectRate } from "~/forms/ProjectBudgetForm/types"

import { useProjectBudgetFormContext } from "../ProjectBudgetFormProvider"
import Column from "../TableColumn"
import Row from "../TableRow"
import { ProjectBudgetFormData } from "../formTypes"

import { calculateBudgetFromRoleMinutesAndRate } from "./project_budget_helpers"

type Props = {
  budgetedRole: BudgetedRole
  rateCardRate: number
  defaultFullTimeMinutes: number
  showDayView: boolean
  canDelete?: boolean
  deleteTooltip?: string
}

const BudgetItemRow = (props: Props) => {
  const {
    budgetedRole,
    rateCardRate,
    defaultFullTimeMinutes,
    showDayView,
    canDelete = false,
    deleteTooltip,
  } = props

  const { watch, setValue } = useFormContext<ProjectBudgetFormData>()
  const formData = watch()

  // Get calculated values and permissions from context
  const { hideFinancials, showNonBillableView } = useProjectBudgetFormContext()

  const hoursPerDay = defaultFullTimeMinutes / 60

  // Find the project rate for this role
  const projectRate = formData.projectRates.find(
    (rate) => rate.roleId === budgetedRole.id,
  )
  const effectiveRate = projectRate?.rate ?? rateCardRate

  const [days, setDays] = useState(
    budgetedRole.estimatedMinutes / defaultFullTimeMinutes,
  )

  const budgetFromEstimatedMinutes = numbro(
    calculateBudgetFromRoleMinutesAndRate(
      budgetedRole.estimatedMinutes,
      effectiveRate,
    ),
  ).format({ thousandSeparated: true, mantissa: 0 })

  const [budgetInput, setBudgetInput] = useState(budgetFromEstimatedMinutes)

  const disableBudget = hideFinancials === true || !Number(effectiveRate)

  useEffect(() => {
    const rate = Number(effectiveRate)
    if (Number.isNaN(rate) || !effectiveRate) {
      return
    }

    setBudgetInput(budgetFromEstimatedMinutes)
  }, [effectiveRate, budgetFromEstimatedMinutes])

  useEffect(() => {
    // Update project rate when rate changes
    const currentRates = formData.projectRates
    const existingRateIndex = currentRates.findIndex(
      (rate) => rate.roleId === budgetedRole.id,
    )

    const rateData: ProjectRate = {
      id: projectRate?.id || null,
      roleId: budgetedRole.id,
      projectId: formData.projectId,
      rate: Number(effectiveRate),
    }

    const updatedRates = [...currentRates]
    if (existingRateIndex !== -1) {
      updatedRates[existingRateIndex] = rateData
    } else {
      updatedRates.push(rateData)
    }

    setValue("projectRates", updatedRates)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveRate,
    budgetInput,
    budgetedRole.id,
    formData.projectId,
    projectRate?.id,
    setValue,
  ])

  useEffect(() => {
    setBudgetInput(budgetFromEstimatedMinutes)
    setDays(budgetedRole.estimatedMinutes / defaultFullTimeMinutes)
  }, [
    budgetedRole.estimatedMinutes,
    budgetFromEstimatedMinutes,
    defaultFullTimeMinutes,
  ])

  const handleMinutes = (minutes: number) => {
    const updatedRoles = formData.roles.map((role) =>
      role.id === budgetedRole.id
        ? { ...role, estimatedMinutes: minutes }
        : role,
    )
    setValue("roles", updatedRoles)
  }

  const handleBudget = (e) => {
    const budget = Number(numbro.unformat(e.target.value))

    if (Number.isNaN(budget)) {
      return setBudgetInput(budgetInput)
    }

    const minutes = Math.round((budget / Number(effectiveRate)) * 60)
    handleMinutes(minutes)
  }

  const formatBudgetInput = (val) => {
    if (!val) {
      return setBudgetInput("0")
    }
    if (!Number(val)) {
      return setBudgetInput(budgetInput)
    }

    const value = numbro(val).format({ thousandSeparated: true, mantissa: 0 })
    setBudgetInput(value)
  }

  const handleRemoveRole = () => {
    if (!canDelete) return

    // Remove role from roles array
    const updatedRoles = formData.roles.filter(
      (role) => role.id !== budgetedRole.id,
    )
    setValue("roles", updatedRoles)

    // Add to deleted roles if it has a projectRoleId
    if (budgetedRole.projectRoleId) {
      const updatedDeletedRoles = [
        ...formData.deletedRoleIds,
        budgetedRole.projectRoleId,
      ]
      setValue("deletedRoleIds", updatedDeletedRoles)
    }

    // Remove associated project rate
    const updatedRates = formData.projectRates.filter(
      (rate) => rate.roleId !== budgetedRole.id,
    )
    setValue("projectRates", updatedRates)
  }

  const formattedRoleName = dashify(budgetedRole.name)

  return (
    <Row dataTest={`${formattedRoleName}-budget-row`}>
      <Column>{budgetedRole.name}</Column>
      {!hideFinancials && !showNonBillableView && (
        <Column>
          <span data-test={`${formattedRoleName}-project-rate`}>
            {formatCurrency(
              showDayView ? effectiveRate * hoursPerDay : effectiveRate,
            )}
          </span>
        </Column>
      )}
      <Column>
        {showDayView ? (
          <NumericInput
            value={days || 0}
            mantissa={2}
            min={0}
            trimMantissa
            onValueChange={(e) => setDays(e)}
            onBlur={() =>
              handleMinutes(Math.round(days * defaultFullTimeMinutes))
            }
            dataTest={`${formattedRoleName}-budget_days`}
          />
        ) : (
          <Duration
            minutesPerDay={budgetedRole.estimatedMinutes || null}
            handleMinutesPerDay={handleMinutes}
            allowZero
            defaultDuration={0}
            dataTest={`${formattedRoleName}-budget_hours`}
            height={30}
            style={{
              borderColor: "var(--winter)",
            }}
          />
        )}
      </Column>
      {!hideFinancials && !showNonBillableView && (
        <Column>
          <CurrencyInput
            name={`${formattedRoleName}-budget_dollar`}
            dataTest={`${formattedRoleName}-budget_dollar`}
            value={budgetInput}
            onChange={formatBudgetInput}
            onBlur={handleBudget}
            currency={currency}
            disabled={disableBudget}
            height={30}
            selectAllOnFocus
          />
        </Column>
      )}
      <div
        className={cc([
          styles.deleteIconContainer,
          !canDelete && styles.disabled,
        ])}
        onClick={handleRemoveRole}
        data-test="budget-role-delete"
      >
        <Delete alt={deleteTooltip} color="var(--winter)" />
      </div>
    </Row>
  )
}
export default BudgetItemRow
